import type { ReactNode } from "react";
import { useEffect } from "react";

import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type {
  AnalyticsPayload,
  AnalyticsFilterPayload,
  AnalyticsOrderPayload,
  AnalyticsSearchPayload,
} from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useFsAnalytics } from "@shared/hooks/useFsAnalytics/useFsAnalytics";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useUnauthUrlFields } from "@shared/hooks/useUnauthUrlFields/useUnauthUrlFields";
import { useLocation } from "@shared/react-router-dom/react-router-dom";

interface Props {
  children: ReactNode;
  isUnauth?: boolean;
}

const PageViewLogger = ({ children, isUnauth }: Props) => {
  const { pathname } = useLocation();
  const logAnalyticsEvent = useAnalyticsLogger();
  const { logEvent } = useFsAnalytics();
  const unauthUrlFields = useUnauthUrlFields();
  const { isEmerson } = useSharedGlobalConfig();

  const getEventName = () => {
    if (isEmerson && isUnauth) {
      return AnalyticsEvent.EMERSON_UNAUTH_PAGE_VIEW;
    } else if (isUnauth) {
      return AnalyticsEvent.UNAUTH_PAGE_VIEW;
    } else if (isEmerson) {
      return AnalyticsEvent.EMERSON_PAGE_VIEW;
    }

    return AnalyticsEvent.STORE_PAGE_VIEW;
  };

  useEffect(() => {
    const payload:
      | AnalyticsPayload
      | AnalyticsSearchPayload
      | AnalyticsFilterPayload
      | AnalyticsOrderPayload = {
      ...unauthUrlFields,
      url: pathname,
      full_url: window.location.href,
      referrer: document.referrer,
    };

    payload.rendered_width_px = Math.round(window?.innerWidth ?? 0);
    payload.rendered_height_px = Math.round(window?.innerHeight ?? 0);

    logAnalyticsEvent({
      eventName: getEventName(),
      payload,
    });

    const pageViewPayload = { ...payload, page_url: window.location.href };
    delete pageViewPayload.url;
    delete pageViewPayload.full_url;

    logAnalyticsEvent({
      eventName: AnalyticsEvent.PAGE_VIEW,
      payload: pageViewPayload,
    });

    logEvent().pageView({
      // oxlint-disable-next-line fullscript-restricted-syntax/no-window-location-origin -- client-only analytics logger
      page_url: window.location.origin + window.location.pathname,
      page_url_full: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      fs_property: isEmerson ? "emerson" : "app",
      auth_status: isUnauth ? "unauth" : "auth",
      is_entry: false, // is_entry is computed in a transformation
    });
  }, [pathname]);

  return <>{children}</>;
};

export { PageViewLogger };
